import { AlertTriangle } from "lucide-react"
import { useTranslation } from "react-i18next"

import { ResendVerificationButton } from "@/components/email-verification/resend-verification-button"
import { Alert, AlertDescription } from "@/components/ui/alert"

/**
 * CollabGateAlert — OKS-146.
 *
 * Inline destructive Alert rendered on a creator collab card when:
 *   1. `currentUser.verification_grace_expired === true`, AND
 *   2. The collab is in a state that would otherwise show one of the
 *      gated actions (`accept`, `apply`, `mark_posted`).
 *
 * Replaces the card's CTA row entirely (per UX Spec §2.3); the rest
 * of the card (title, brand, deadline, state badge) renders normally
 * so the creator still sees what they're blocked on.
 *
 * Embeds a `<ResendVerificationButton>` at its default size. Per-card
 * cooldown is intentional V1 simplicity — server cooldown is the
 * source of truth (UX Spec §3.3 + §9.5 lock).
 *
 * OKS-874 — this previously passed `size="sm"` (32px) and this comment
 * previously called that "compact", i.e. deliberate. It was overridden:
 * the collab gate is a BLOCKING alert on the engagement loop, and a
 * creator who cannot proceed until they act is the last user who should
 * get the smallest target in the product. The prop is dropped rather
 * than the primitive changed — `sm` is NOT floored by OKS-874, so this
 * reaches 44px only by falling through to `default`.
 *
 * @param collabId — included in the test id so multi-card surfaces
 *   can target a specific alert (`collab-gate-alert-{id}`).
 */
interface CollabGateAlertProps {
  collabId: number
  className?: string
}

export function CollabGateAlert({ collabId, className }: CollabGateAlertProps) {
  const { t } = useTranslation()

  return (
    <Alert
      variant="destructive"
      role="alert"
      data-testid={`collab-gate-alert-${collabId}`}
      className={className}
    >
      <AlertTriangle aria-hidden="true" />
      <AlertDescription className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
        <span className="flex-1">
          {t("email_verification.gate.alert_body")}
        </span>
        <ResendVerificationButton />
      </AlertDescription>
    </Alert>
  )
}
